/* @bend-expect quartz-no-create-browser-history */
import { createBrowserHistory, type History } from './react-router';
import {
  createGraniteRemoteHistory,
  type GraniteRemoteHistory,
} from './createGraniteRemoteHistory';
import { set } from 'quartz/globalState/api';
import {
  HISTORY_INSTANCE,
  HISTORY_BASE_PATH,
  type HistoryBasePath,
} from './globalState';
export type AttachHistory = (history: History, basename: string) => void;

function matchPattern(
  pattern: string,
  pathname: string
): HistoryBasePath | undefined {
  if (pattern === '/') {
    if (pathname.startsWith('/')) {
      return { basePathname: '/', params: {} };
    }
    return undefined;
  }

  const patternSegments = pattern.split('/');
  const pathSegments = pathname.split('/');

  if (pathSegments.length < patternSegments.length) {
    return undefined;
  }

  const params: Record<string, string> = {};
  const matchedSegments: string[] = [];

  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const pathSegment = pathSegments[i];

    if (patternSegment.startsWith(':')) {
      const paramName = patternSegment.slice(1);
      if (!paramName) {
        throw new TypeError(
          `Invalid historyBasename pattern '${pattern}'. Missing parameter name.`
        );
      }
      if (!pathSegment) {
        return undefined;
      }
      params[paramName] = pathSegment;
      matchedSegments.push(pathSegment);
    } else if (patternSegment === pathSegment) {
      matchedSegments.push(pathSegment);
    } else {
      return undefined;
    }
  }

  return {
    basePathname: matchedSegments.join('/'),
    params,
  };
}

export function findBasePath(
  historyBasename: string | string[],
  currentPathname: string
): HistoryBasePath | undefined {
  const basenames = Array.isArray(historyBasename)
    ? historyBasename
    : [historyBasename];

  for (const pattern of basenames) {
    const match = matchPattern(pattern, currentPathname);
    if (match) {
      return match;
    }
  }
}

export function initializeRemoteHistory(
  historyBasename: string | string[],
  attachHistory: AttachHistory
): History {
  const basePath = findBasePath(historyBasename, window.location.pathname);

  if (!basePath) {
    throw new Error('Browser url does not match `historyBasename`');
  }

  const relativePath =
    window.location.pathname.slice(basePath.basePathname.length) || '/';

  const history = createGraniteRemoteHistory(
    basePath.basePathname,
    relativePath,
    window.location.search
  );

  set(HISTORY_INSTANCE, history);
  set(HISTORY_BASE_PATH, basePath);

  attachHistory(history, basePath.basePathname);
  return history;
}

export type BrowserHistoryAPI = Pick<
  typeof window.history,
  'pushState' | 'replaceState' | 'go' | 'state' | 'length'
>;

export function syncHistoryToBrowser(
  remoteHistory: GraniteRemoteHistory,
  browserHistory: BrowserHistoryAPI = window.history
): () => void {
  return remoteHistory._connect(browserHistory);
}

export function initializeHistory(historyBasename: string | string[]): History {
  const basePath = findBasePath(historyBasename, window.location.pathname);

  if (!basePath) {
    throw new Error('Browser url does not match `historyBasename`');
  }

  const history: History = createBrowserHistory({
    basename: basePath.basePathname,
  });

  set(HISTORY_INSTANCE, history);
  set(HISTORY_BASE_PATH, basePath);

  return history;
}
